import React, { createContext, useContext } from "react";

import {
  usePressClassificationFilterQuery,
  usePressClassificationNameQuery,
  usePressClassificationQuery,
} from "@api/pressClassification/pressClassification";

type ClassificationResponse = {
  code: string;
  name: string;
  isCheck: boolean;
};

type ClassificationContextType = {
  pressClassificationData: Record<string, string> | null;
  pressClassificationNameData: Record<string, string> | null;
  pressClassificationFilterData: {
    ai: ClassificationResponse[];
    manual: ClassificationResponse[];
  } | null;
  isLoading: boolean;
};

const ClassificationContext = createContext<ClassificationContextType | null>(
  null,
);

export const ClassificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data: pressClassificationData, isLoading: isClassificationLoading } =
    usePressClassificationQuery();

  const { data: pressClassificationNameData, isLoading: isNameLoading } =
    usePressClassificationNameQuery();
  const { data: pressClassificationFilterData, isLoading: isFilterLoading } =
    usePressClassificationFilterQuery();

  const isLoading = isClassificationLoading || isNameLoading || isFilterLoading;

  const value = React.useMemo(
    () => ({
      pressClassificationData: pressClassificationData || null,
      pressClassificationNameData: pressClassificationNameData || null,
      pressClassificationFilterData: pressClassificationFilterData || null,
      isLoading,
    }),
    [
      pressClassificationData,
      pressClassificationNameData,
      pressClassificationFilterData,
      isLoading,
    ],
  );

  return (
    <ClassificationContext.Provider value={value}>
      {children}
    </ClassificationContext.Provider>
  );
};

export const useClassification = () => {
  const context = useContext(ClassificationContext);
  if (!context) {
    throw new Error(
      "useClassification must be used within a ClassificationProvider",
    );
  }
  return context;
};
